import React from 'react';
import {
  Users, DollarSign, Box, LifeBuoy, TrendingUp, Settings, BarChart2, Mail,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  AssetsMaintenanceWidget, AssetsOverviewWidget, AssetsRegistryWidget, AttendanceSummaryWidget, AttendanceTrendWidget,
  CampaignsWidget, ExpensesWidget, FinanceChartWidget, FinanceOverviewWidget, InvoicesWidget, LeadsWidget, LeaveWidget,
  PayrollWidget, PeopleOverviewWidget, ProjectsWidget, RecentActivityWidget, SecurityWidget, SupportOverviewWidget,
  SupportSlaWidget, TasksWidget, TimesheetsWidget, UsersWidget,
} from './LiveWidgets';

/**
 * Widgets available on the main dashboard. Every data widget reads live data
 * (see LiveWidgets.tsx); the type keys are unchanged so saved layouts keep working.
 */

const QUICK_HUBS = [
  { title: 'Assets', icon: Box, path: '/assets' },
  { title: 'People', icon: Users, path: '/people' },
  { title: 'Growth', icon: TrendingUp, path: '/growth' },
  { title: 'Finance', icon: DollarSign, path: '/money' },
  { title: 'Operations', icon: Settings, path: '/work' },
  { title: 'Analytics', icon: BarChart2, path: '/admin' },
  { title: 'Marketing', icon: Mail, path: '/growth/marketing' },
  { title: 'Support', icon: LifeBuoy, path: '/support' },
];

export function QuickHubsWidget() {
  const navigate = useNavigate();
  return (
    <div className="h-full w-full p-4 flex flex-col justify-center">
      <div className="flex flex-wrap gap-2">
        {QUICK_HUBS.map((hub) => (
          <button
            key={hub.title}
            onClick={() => navigate(hub.path)}
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted hover:bg-muted/80 transition-all outline-none"
          >
            <hub.icon className="h-3 w-3 text-primary" />
            <span className="text-xs font-medium">{hub.title}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

import {
  EndUserMyRequestsWidget,
  EndUserServiceCatalogWidget,
  EndUserMyAssetsWidget,
  EndUserSelfServiceWidget,
} from './EndUserWidgets';

// --- Registry ---

export type WidgetGroup = 'People' | 'Assets' | 'Support' | 'Work' | 'Finance' | 'Growth' | 'Admin' | 'General';
export const WIDGET_GROUPS: WidgetGroup[] = ['People', 'Assets', 'Support', 'Work', 'Finance', 'Growth', 'Admin', 'General'];

export const WidgetRegistry: Record<string, {
  title: string; group: WidgetGroup; description: string; component: React.FC<any>; defaultW: number; defaultH: number;
}> = {
  'overview-finance': { title: 'Finance overview', group: 'Finance', description: 'Outstanding and overdue invoices', component: FinanceOverviewWidget, defaultW: 3, defaultH: 3 },
  'overview-people': { title: 'People & HR', group: 'People', description: 'Headcount, joiners and onboarding', component: PeopleOverviewWidget, defaultW: 3, defaultH: 3 },
  'overview-support': { title: 'Support tickets', group: 'Support', description: 'Open tickets by team queue', component: SupportOverviewWidget, defaultW: 3, defaultH: 3 },
  'overview-assets': { title: 'Assets', group: 'Assets', description: 'Assigned, available and other assets', component: AssetsOverviewWidget, defaultW: 3, defaultH: 3 },
  'chart-attendance': { title: 'Attendance trend', group: 'People', description: 'People present per day over two weeks', component: AttendanceTrendWidget, defaultW: 8, defaultH: 5 },
  'chart-finance': { title: 'Invoices (6 months)', group: 'Finance', description: 'Invoiced vs paid, last six months', component: FinanceChartWidget, defaultW: 6, defaultH: 4 },
  'recent-activity': { title: 'Recent activity', group: 'General', description: 'Your latest notifications', component: RecentActivityWidget, defaultW: 4, defaultH: 4 },
  'quick-hubs': { title: 'Quick access hubs', group: 'General', description: 'Shortcuts to every hub', component: QuickHubsWidget, defaultW: 6, defaultH: 2 },
  'hr-attendance': { title: 'Attendance today', group: 'People', description: 'Who is in, late or missing today', component: AttendanceSummaryWidget, defaultW: 4, defaultH: 4 },
  'hr-leave': { title: 'Leave', group: 'People', description: 'Pending requests and your balances', component: LeaveWidget, defaultW: 3, defaultH: 3 },
  'hr-payroll': { title: 'Payroll', group: 'People', description: "This month's payroll run and your payslip", component: PayrollWidget, defaultW: 3, defaultH: 3 },
  'assets-registry': { title: 'Assets by category', group: 'Assets', description: 'Assets by category, assigned vs free', component: AssetsRegistryWidget, defaultW: 3, defaultH: 3 },
  'assets-maintenance': { title: 'Maintenance schedule', group: 'Assets', description: 'Upcoming repairs and servicing', component: AssetsMaintenanceWidget, defaultW: 3, defaultH: 3 },
  'money-invoices': { title: 'Open invoices', group: 'Finance', description: 'Unpaid invoices by due date', component: InvoicesWidget, defaultW: 3, defaultH: 3 },
  'money-expenses': { title: 'Monthly expenses', group: 'Finance', description: 'Spending this month by category', component: ExpensesWidget, defaultW: 3, defaultH: 3 },
  'work-projects': { title: 'Active projects', group: 'Work', description: 'Active projects and due dates', component: ProjectsWidget, defaultW: 3, defaultH: 3 },
  'work-tasks': { title: 'Tasks', group: 'Work', description: 'Your open and overdue tasks', component: TasksWidget, defaultW: 4, defaultH: 4 },
  'work-timesheets': { title: 'Timesheet approvals', group: 'Work', description: 'Timesheets waiting for approval', component: TimesheetsWidget, defaultW: 3, defaultH: 3 },
  'support-sla': { title: 'SLA breaches', group: 'Support', description: 'Tickets past their SLA', component: SupportSlaWidget, defaultW: 3, defaultH: 3 },
  'growth-leads': { title: 'New sales leads', group: 'Growth', description: 'New leads in the last 30 days', component: LeadsWidget, defaultW: 3, defaultH: 3 },
  'growth-marketing': { title: 'Active campaigns', group: 'Growth', description: 'Campaigns running now', component: CampaignsWidget, defaultW: 3, defaultH: 3 },
  'admin-users': { title: 'User accounts', group: 'Admin', description: 'Active accounts and approvals', component: UsersWidget, defaultW: 3, defaultH: 3 },
  'admin-security': { title: 'Security alerts', group: 'Admin', description: 'Failed sign-ins and anomalies', component: SecurityWidget, defaultW: 3, defaultH: 3 },
};
